import React, { useState, useEffect, useRef } from "react";
import { profileData } from "../data/mock";
import { Button } from "./ui/button";
import { ArrowDown, Mail, Linkedin, MapPin, TrendingUp, Download } from "lucide-react";
import { generateResumeDocx } from "../utils/generateResumeDocx";

/* ─── Role-based stacked bar for "10+ Years" ─── */
const roles = [
  { label: "Trailer Util.", years: 1.6, color: "#2dd4bf" },
  { label: "Pricing Analyst", years: 4.4, color: "#14b8a6" },
  { label: "Account Mgr.", years: 2.6, color: "#0d9488" },
  { label: "Capacity Coord.", years: 1.4, color: "#0f766e" },
];
const totalYears = roles.reduce((s, r) => s + r.years, 0);

const YearsChart = ({ isVisible }) => {
  // Cumulative years so bars always increase
  const cumulative = roles.reduce((acc, role, i) => {
    const prev = i > 0 ? acc[i - 1] : 0;
    acc.push(prev + role.years);
    return acc;
  }, []);
  const maxVal = cumulative[cumulative.length - 1];

  return (
    <div className="group flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm border border-slate-100 hover:border-teal-100 hover:shadow-md transition-all duration-300 h-28 w-full">
      <div className="flex items-end gap-[4px] h-10 shrink-0">
        {cumulative.map((total, i) => (
          <div
            key={i}
            className="w-[6px] rounded-t-sm transition-all ease-out"
            style={{
              height: isVisible
                ? `${(total / maxVal) * 100}%`
                : "6%",
              backgroundColor: roles[i].color,
              transitionDuration: `${700 + i * 150}ms`,
              transitionDelay: `${200 + i * 120}ms`,
            }}
            title={`${roles[i].label}: ${total} yrs total`}
          />
        ))}
      </div>
      <p className="text-lg font-bold text-slate-900 font-serif leading-none mt-1.5 group-hover:text-teal-700 transition-colors text-center">
        10+
      </p>
      <p className="text-[10px] text-slate-500 mt-0.5 leading-snug text-center">
        Years Experience
      </p>
    </div>
  );
};

/* ─── Mini bar chart for "97.8%" (unchanged style) ─── */
const PerformanceChart = ({ isVisible }) => {
  const barHeights = [45, 62, 70, 85, 98];
  return (
    <div className="group flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm border border-slate-100 hover:border-teal-100 hover:shadow-md transition-all duration-300 h-28 w-full">
      <div className="flex items-end gap-[3px] h-10 shrink-0">
        {barHeights.map((h, i) => (
          <div
            key={i}
            className="w-[5px] rounded-t-sm transition-all ease-out"
            style={{
              height: isVisible ? `${h}%` : "8%",
              backgroundColor: "#0d9488",
              opacity: 0.45 + (i / barHeights.length) * 0.55,
              transitionDuration: `${600 + i * 150}ms`,
              transitionDelay: `${200 + i * 80}ms`,
            }}
          />
        ))}
      </div>
      <p className="text-lg font-bold text-slate-900 font-serif leading-none mt-1.5 group-hover:text-teal-700 transition-colors text-center">
        ~98%
      </p>
      <p className="text-[10px] text-slate-500 mt-0.5 leading-snug text-center">
        On-Time Performance
      </p>
    </div>
  );
};

/* ─── Dashboard gauge for "550+" monthly loads ─── */
const RevenueGauge = ({ isVisible }) => {
  const [animatedAngle, setAnimatedAngle] = useState(0);
  const targetAngle = 180 * 0.79; // 550/700 capacity

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setAnimatedAngle(targetAngle), 400);
      return () => clearTimeout(timer);
    }
  }, [isVisible, targetAngle]);

  const radius = 38;
  const strokeWidth = 7;
  const cx = 50;
  const cy = 48;

  const polarToCartesian = (angle) => {
    const rad = ((180 + angle) * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    };
  };

  const describeArc = (startAngle, endAngle) => {
    const start = polarToCartesian(startAngle);
    const end = polarToCartesian(endAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  return (
    <div className="group flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm border border-slate-100 hover:border-teal-100 hover:shadow-md transition-all duration-300 h-28 w-full">
      <div className="relative" style={{ maxWidth: 64 }}>
        <svg viewBox="0 0 100 58" className="w-full">
          <path d={describeArc(0, 180)} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} strokeLinecap="round" />
          <path d={describeArc(0, animatedAngle)} fill="none" stroke="url(#gaugeGrad)" strokeWidth={strokeWidth} strokeLinecap="round" style={{ transition: "all 1.4s cubic-bezier(0.22, 1, 0.36, 1)" }} />
          {Array.from({ length: 5 }).map((_, i) => {
            const angle = (i / 4) * 180;
            const outer = polarToCartesian(angle);
            const innerR = radius - 10;
            const rad = ((180 + angle) * Math.PI) / 180;
            const inner = { x: cx + innerR * Math.cos(rad), y: cy + innerR * Math.sin(rad) };
            return <line key={i} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="#cbd5e1" strokeWidth={0.6} />;
          })}
          <defs>
            <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2dd4bf" />
              <stop offset="100%" stopColor="#0f766e" />
            </linearGradient>
          </defs>
          <text x={cx} y={cy - 4} textAnchor="middle" className="font-serif font-bold" fill="#0f172a" fontSize="13">550+</text>
          <text x={cx} y={cy + 7} textAnchor="middle" fill="#94a3b8" fontSize="5">loads / month</text>
        </svg>
      </div>
      <p className="text-[9px] text-slate-500 mt-0 text-center leading-snug">
        Monthly Loads Coordinated
      </p>
    </div>
  );
};

/* ─── Up arrow for "25%" productivity ─── */
const ProductivityArrow = ({ isVisible }) => {
  return (
    <div className="group flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm border border-slate-100 hover:border-teal-100 hover:shadow-md transition-all duration-300 h-28 w-full">
      <div
        className="relative transition-all ease-out"
        style={{
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          opacity: isVisible ? 1 : 0,
          transitionDuration: "800ms",
          transitionDelay: "400ms",
        }}
      >
        <div className="flex flex-col items-center">
          <div
            className="transition-all ease-out"
            style={{
              transform: isVisible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.8)",
              transitionDuration: "600ms",
              transitionDelay: "600ms",
            }}
          >
            <TrendingUp
              size={24}
              className="text-teal-500 group-hover:text-teal-600 transition-colors"
              strokeWidth={2.5}
            />
          </div>
          <p className="text-lg font-bold text-slate-900 font-serif leading-none mt-1 group-hover:text-teal-700 transition-colors text-center">
            {"<5min"}
          </p>
        </div>
      </div>
      <p className="text-[10px] text-slate-500 mt-0.5 text-center leading-snug">
        Quote Turnaround
      </p>
    </div>
  );
};

/* ─── Main Hero ─── */
const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = (href) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section className="relative flex items-center overflow-hidden bg-slate-50">
      {/* Background video - contained to hero only, below header */}
      <div className="absolute top-16 left-0 right-0 bottom-0 overflow-hidden rounded-b-3xl">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
          style={{ opacity: 0.5 }}
          src={`${process.env.PUBLIC_URL}/hero-video-hd.mp4`}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/55 via-white/45 to-teal-50/35" />
      </div>

      <div className="absolute top-20 right-0 w-96 h-96 bg-teal-100/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-slate-100/40 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-24 pb-12 lg:pt-28 lg:pb-14 w-full relative z-10">
        <div className="space-y-5">
          {/* Hero Content */}
          <div className="max-w-2xl space-y-5">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50/90 border border-teal-100 rounded-full text-teal-700 text-xs font-medium tracking-wide">
                <MapPin size={13} />
                {profileData.location}
              </div>
            </div>

            <div className="space-y-3">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                {profileData.name}
              </h1>
              <p className="text-lg sm:text-xl text-teal-700 font-medium">
                {profileData.title}
              </p>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                {profileData.tagline}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-6 h-11 text-sm font-medium"
                onClick={() => handleScroll("#contact")}
              >
                <Mail size={16} className="mr-2" />
                Contact Me
              </Button>
              <Button
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-white/80 rounded-lg px-6 h-11 text-sm font-medium bg-white/60 backdrop-blur-sm"
                onClick={() => handleScroll("#experience")}
              >
                View Experience
                <ArrowDown size={16} className="ml-2" />
              </Button>
              <a
                href={profileData.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-11 w-11 rounded-lg border border-slate-300 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all bg-white/60 backdrop-blur-sm"
              >
                <Linkedin size={18} />
              </a>
              <Button
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-200 rounded-lg px-6 h-11 text-sm font-medium bg-white/60 backdrop-blur-sm"
                onClick={generateResumeDocx}
              >
                <Download size={16} className="mr-2" />
                Download Resume
              </Button>
            </div>
          </div>

          {/* Stats row */}
          <div ref={statsRef}>
            <div className="grid grid-cols-4 gap-2 max-w-md">
              <YearsChart isVisible={isVisible} />
              <PerformanceChart isVisible={isVisible} />
              <RevenueGauge isVisible={isVisible} />
              <ProductivityArrow isVisible={isVisible} />
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;
